import { useState } from "react";
import { X, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useSubscriptionValidation } from "@/hooks/useSubscriptionValidation";

interface FlashcardGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFlashcardsGenerated: (flashcards: any[], metadata: any, userPrompt: string) => void;
}

export const FlashcardGenerationModal = ({ isOpen, onClose, onFlashcardsGenerated }: FlashcardGenerationModalProps) => {
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();
  const { validateCreditsForAction } = useSubscriptionValidation();

  const handleSubmit = async () => {
    if (!prompt.trim()) {
      toast({
        title: "Error",
        description: "Please enter a topic or description for your flashcards",
        variant: "destructive"
      });
      return;
    }

    // Validate credits before generation
    const creditsValidation = validateCreditsForAction();
    if (!creditsValidation.canProceed) {
      // Trigger out of credits dialog
      window.dispatchEvent(new CustomEvent('open_out_of_credits_dialog'));
      return;
    }

    setIsGenerating(true);

    try {
      // Get the current session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No valid session found. Please log in again.');
      }

      // Call the backend API to generate flashcards
      const response = await fetch('http://localhost:8080/api/generate/flashcards', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          prompt: prompt.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('API Error Response:', data);
        throw new Error(data.error || data.details || 'Failed to generate flashcards');
      }

      if (!data.success) {
        console.error('API returned success=false:', data);
        throw new Error(data.error || data.details || 'Failed to generate flashcards');
      }

      // Validate response data
      if (!data.flashcards || !Array.isArray(data.flashcards) || data.flashcards.length === 0) {
        console.error('Invalid flashcards response:', data);
        throw new Error('No flashcards were generated. Please try a different prompt.');
      }

      // Success - pass the flashcards to the parent component
      onFlashcardsGenerated(data.flashcards, data.metadata || {}, prompt);
      
      toast({
        title: "Success",
        description: `Generated ${data.flashcards?.length || 0} flashcards successfully!`
      });

      // Reset form and close modal
      setPrompt("");
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate flashcards';
      
      let userMessage = "Failed to generate flashcards. Please try again.";
      if (errorMessage.includes('No valid session found')) {
        userMessage = 'Please log in again to generate flashcards.';
      } else if (errorMessage.includes('AI service is not available')) {
        userMessage = 'AI service is temporarily unavailable. Please try again later.';
      } else if (errorMessage.includes('Failed to parse AI response')) {
        userMessage = 'AI response was invalid. Please try again with a different prompt.';
      } else if (errorMessage.includes('Network error') || errorMessage.includes('fetch')) {
        userMessage = 'Network error. Please check your connection and try again.';
      }
      
      toast({
        title: "Error",
        description: userMessage,
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    setPrompt("");
    setIsGenerating(false);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-slate-900">Generate Flashcards</h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isGenerating}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="flashcard-prompt" className="block text-sm font-medium text-slate-700 mb-2">
              Topic & Requirements
            </label>
            <Textarea
              id="flashcard-prompt"
              placeholder="Describe the topic you want flashcards for. Be specific about the subject, difficulty level, and any particular aspects you want to focus on.

Examples:
• Create flashcards for basic Spanish vocabulary about food and restaurants
• Generate intermediate-level flashcards about photosynthesis for high school biology
• Make flashcards covering the main concepts from the American Civil War"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isGenerating}
              className="w-full min-h-[120px] resize-none"
              rows={6}
            />
          </div>
          
          <div className="text-xs text-slate-500">
            <p className="font-medium mb-1">Tips for better flashcards:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Be specific about the topic and difficulty level</li>
              <li>Mention if you want definitions, examples, or application questions</li>
              <li>Include context like "for beginners" or "advanced level"</li>
              <li>Press Cmd/Ctrl + Enter to generate</li>
            </ul>
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isGenerating}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isGenerating || !prompt.trim()}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Flashcards
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};